import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { listAnalyses, getBilling, gaData, createAnalysis, setEmailPrefs, sendTestWeeklyEmail } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { ArrowUpRight, Plus, Sparkles, Clock, ChevronRight, Zap, Link2, Mail } from "lucide-react";
import { format, subDays } from "date-fns";

const toISO = (d) => format(d, "yyyy-MM-dd");
const fmtDate = (d) => format(d, "MMM d");

export default function Dashboard() {
  const [items, setItems] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [oneClickBusy, setOneClickBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([listAnalyses(), getBilling()]);
        setItems(a);
        setBilling(b);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleWeeklyEmail = async (next) => {
    const prev = billing?.weekly_email_enabled;
    setBilling((b) => ({ ...b, weekly_email_enabled: next }));
    try {
      await setEmailPrefs(next);
      toast.success(next ? "Monday emails on" : "Monday emails off");
    } catch (err) {
      setBilling((b) => ({ ...b, weekly_email_enabled: prev }));
      toast.error(err?.response?.data?.detail || "Could not update preference");
    }
  };

  const sendTestEmail = async () => {
    setEmailBusy(true);
    try {
      await sendTestWeeklyEmail();
      toast.success("Email sent — check your inbox");
      const b = await getBilling();
      setBilling(b);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.reason || "Could not send test email");
      toast.error(msg, { duration: 8000 });
    } finally {
      setEmailBusy(false);
    }
  };

  const runOneClickWeekly = async () => {
    if (!billing?.ga_default_property_id) return;
    setOneClickBusy(true);
    try {
      const today = new Date();
      const curFrom = subDays(today, 7);
      const curTo = subDays(today, 1);
      const prevFrom = subDays(today, 14);
      const prevTo = subDays(today, 8);

      const data = await gaData({
        property_id: billing.ga_default_property_id,
        current_start: toISO(curFrom),
        current_end: toISO(curTo),
        previous_start: toISO(prevFrom),
        previous_end: toISO(prevTo),
      });

      const propLabel = billing.ga_default_property_name
        ? `${billing.ga_default_property_name}${billing.ga_default_account_name ? ` (${billing.ga_default_account_name})` : ""}`
        : "Google Analytics";

      const createPayload = {
        source: "ga",
        property_label: propLabel,
        ga_property_id: billing.ga_default_property_id,
        current_period: { ...data.current.period, label: `${fmtDate(curFrom)} – ${fmtDate(curTo)} (this week)` },
        previous_period: { ...data.previous.period, label: `${fmtDate(prevFrom)} – ${fmtDate(prevTo)} (last week)` },
        metrics_current: data.current.metrics,
        metrics_previous: data.previous.metrics,
        timeseries_current: data.current.timeseries,
        timeseries_previous: data.previous.timeseries,
      };
      const analysis = await createAnalysis(createPayload);
      toast.success("Weekly check ready");
      navigate(`/analysis/${analysis.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not run weekly check", { duration: 8000 });
    } finally {
      setOneClickBusy(false);
    }
  };

  const planLabel = billing?.plan === "agency"
    ? "Agency White-Label"
    : billing?.plan === "monthly"
      ? "Business Unlimited"
      : billing?.credits > 0
        ? `${billing.credits} credit${billing.credits === 1 ? "" : "s"} left`
        : "Free";

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 md:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="tag-label text-[#E27D60]">Dashboard</div>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">Welcome back.</h1>
            <p className="text-[#1E392A]/60 mt-1">Pick a date range, drop in your data, and we'll do the rest.</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="rounded-full px-3 py-1.5" data-testid="plan-badge">
              {planLabel}
            </Badge>
            <Button
              onClick={() => navigate("/analyze")}
              data-testid="new-analysis-btn"
              className="rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white h-11 px-5"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New analysis
            </Button>
          </div>
        </div>

        {/* One-click weekly check (GA connected + has default property + has access) */}
        {billing?.has_access && billing?.ga_connected && billing?.ga_default_property_id && (
          <div className="rounded-3xl bg-gradient-to-br from-[#1E392A] to-[#152b20] text-[#F7F5F0] p-7 md:p-8 bento-shadow mb-6">
            <div className="flex flex-wrap items-center gap-6">
              <div className="w-14 h-14 rounded-2xl bg-[#E27D60] grid place-items-center shrink-0">
                <Zap className="w-6 h-6 text-white" fill="white" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="tag-label text-[#85CDCB]">Monday-morning ritual</div>
                <div className="font-display text-2xl md:text-3xl mt-1">This week vs last week</div>
                <p className="text-[#F7F5F0]/70 mt-1 text-sm">
                  {billing.ga_default_property_name || "Your GA property"} · {fmtDate(subDays(new Date(), 7))} → {fmtDate(subDays(new Date(), 1))}
                </p>
              </div>
              <Button
                onClick={runOneClickWeekly}
                disabled={oneClickBusy}
                className="rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white h-12 px-6"
                data-testid="one-click-weekly-btn"
              >
                {oneClickBusy ? "Running…" : <>Run now <ArrowUpRight className="ml-1.5 w-4 h-4" /></>}
              </Button>
            </div>

            <div className="mt-6 pt-5 border-t border-white/10 flex flex-wrap items-center gap-4 justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/10 grid place-items-center shrink-0">
                  <Mail className="w-4 h-4 text-[#85CDCB]" />
                </div>
                <div>
                  <div className="text-sm font-medium">Email me every Monday morning</div>
                  <div className="text-xs text-[#F7F5F0]/55">
                    {billing.last_weekly_email_at
                      ? `Last sent ${new Date(billing.last_weekly_email_at).toLocaleString()}`
                      : "We'll send the report to your inbox automatically."}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={sendTestEmail}
                  disabled={emailBusy}
                  data-testid="send-test-email-btn"
                  className="text-sm text-[#85CDCB] hover:underline disabled:opacity-50"
                >
                  {emailBusy ? "Sending…" : "Send a test now"}
                </button>
                <Switch
                  checked={!!billing.weekly_email_enabled}
                  onCheckedChange={toggleWeeklyEmail}
                  data-testid="weekly-email-toggle"
                />
              </div>
            </div>
          </div>
        )}

        {billing?.has_access && billing?.ga_connected && !billing?.ga_default_property_id && (
          <div className="rounded-3xl bg-white border border-dashed border-[#1E392A]/15 p-6 mb-6 flex flex-wrap items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#F7F5F0] grid place-items-center shrink-0">
              <Link2 className="w-5 h-5 text-[#E27D60]" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="font-display text-lg">Pick a default GA property to enable one-click</div>
              <p className="text-sm text-[#1E392A]/65">Choose your main site once and we'll keep the "this week vs last week" button right here.</p>
            </div>
            <Button onClick={() => navigate("/analyze")} className="rounded-full bg-[#1E392A] hover:bg-[#152b20] text-white" data-testid="pick-default-property-btn">
              Pick property
            </Button>
          </div>
        )}

        {/* Quick start */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10">
          {items.length === 0 ? (
            <div className="md:col-span-2 rounded-3xl bg-white border border-black/5 p-7 bento-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#F7F5F0] grid place-items-center">
                  <Sparkles className="w-5 h-5 text-[#E27D60]" />
                </div>
                <div className="flex-1">
                  <div className="font-display text-2xl">Run your first analysis</div>
                  <p className="text-[#1E392A]/65 mt-1">Upload a GA4 CSV export or try the demo dataset. We'll compare any two periods and explain what changed.</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button onClick={() => navigate("/analyze")} data-testid="quick-start-btn" className="rounded-full bg-[#1E392A] hover:bg-[#152b20] text-white">
                      Start analysis <ArrowUpRight className="ml-1.5 w-4 h-4" />
                    </Button>
                    <Link to="/billing" className="text-sm self-center underline underline-offset-4 text-[#1E392A]/70" data-testid="manage-plan-link">Manage plan</Link>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="md:col-span-2 rounded-3xl bg-white border border-black/5 p-7 bento-shadow" data-testid="dashboard-resume-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#F7F5F0] grid place-items-center">
                  <Sparkles className="w-5 h-5 text-[#E27D60]" />
                </div>
                <div className="flex-1">
                  <div className="font-display text-2xl">Pick up where you left off</div>
                  <p className="text-[#1E392A]/65 mt-1">
                    You've run {items.length} {items.length === 1 ? "analysis" : "analyses"}. Run a new one or revisit a past report below.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button onClick={() => navigate("/analyze")} data-testid="quick-start-btn" className="rounded-full bg-[#1E392A] hover:bg-[#152b20] text-white">
                      Start new analysis <ArrowUpRight className="ml-1.5 w-4 h-4" />
                    </Button>
                    <Link to="/billing" className="text-sm self-center underline underline-offset-4 text-[#1E392A]/70" data-testid="manage-plan-link">Manage plan</Link>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="rounded-3xl bg-[#1E392A] text-[#F7F5F0] p-7 bento-shadow">
            <div className="tag-label text-[#85CDCB]">Your plan</div>
            <div className="font-display text-3xl mt-2">{planLabel}</div>
            <p className="text-[#F7F5F0]/70 mt-2 text-sm">
              {billing?.has_access
                ? "You're ready to run analyses."
                : "Subscribe for unlimited analyses + automatic Monday emails."}
            </p>
            <Link to="/billing" className="mt-5 inline-flex items-center text-sm gap-1 text-[#85CDCB] hover:underline" data-testid="upgrade-link">
              {billing?.plan === "monthly" || billing?.plan === "agency" ? "Manage subscription" : "Upgrade"} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* History */}
        <div className="rounded-3xl bg-white border border-black/5 bento-shadow overflow-hidden">
          <div className="px-7 py-5 border-b border-black/5 flex items-center justify-between">
            <div>
              <div className="tag-label text-[#1E392A]/55">History</div>
              <div className="font-display text-2xl mt-1">Past analyses</div>
            </div>
            <Clock className="w-5 h-5 text-[#1E392A]/40" />
          </div>
          {loading ? (
            <div className="p-7 text-[#1E392A]/50">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center">
              <div className="font-display text-xl">Nothing here yet</div>
              <p className="text-[#1E392A]/60 mt-1">Your past analyses will appear here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-black/5">
              {items.map((a) => {
                const topAlert = Array.isArray(a.alerts) && a.alerts.length > 0 ? a.alerts[0] : null;
                return (
                <li key={a.id}>
                  <Link
                    to={`/analysis/${a.id}`}
                    data-testid={`analysis-row-${a.id}`}
                    className="flex items-center justify-between gap-4 px-7 py-5 hover:bg-[#F7F5F0] transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="font-display text-lg truncate">
                        {a.ai?.headline || `${a.current_period?.label || "Period"} vs ${a.previous_period?.label || "previous"}`}
                      </div>
                      <div className="text-sm text-[#1E392A]/60 mt-0.5 truncate">
                        {a.property_label || "Untitled site"} · {new Date(a.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {topAlert && (
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded-full ${
                            topAlert.severity === "warning"
                              ? "bg-[#FFE9DF] text-[#a8492a]"
                              : "bg-[#E4F4EE] text-[#1E392A]"
                          }`}
                          data-testid={`history-alert-${a.id}`}
                        >
                          {topAlert.emoji} {topAlert.label} {topAlert.direction === "down" ? "↓" : "↑"} {Math.abs(topAlert.change_pct).toFixed(0)}%
                        </span>
                      )}
                      <Badge variant="outline" className="rounded-full">{a.source === "demo" ? "Demo" : a.source === "ga" ? "GA" : "CSV"}</Badge>
                      <ChevronRight className="w-4 h-4 text-[#1E392A]/40" />
                    </div>
                  </Link>
                </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
